import { Fragment } from 'react'
import { useTranslation } from '@/i18n'
import { X, FileText, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { MockNode, NodeAttribute } from './types'

export interface NodeConnectionLink {
  id: string
  label: string
  targetId: string
}

interface Props {
  node: MockNode | null
  /** Outgoing edges from this node — used for the Connections section */
  connections: NodeConnectionLink[]
  onClose: () => void
  onConnectionClick?: (targetId: string) => void
  /**
   * Select a connection in place instead of navigating to its other end
   * (DSGN-63 design): the chip turns teal and the canvas highlights that edge.
   * When given, it takes over the chip's click from `onConnectionClick`.
   */
  onConnectionSelect?: (connectionId: string) => void
  /** The connection chip drawn selected. */
  selectedConnectionId?: string | null
  /** Open the document this node represents. Hidden when no docId resolves. */
  onOpenPreview?: () => void
  /**
   * Open a file an attribute names (an equipment's Home sheet / Appears on).
   * Without it those files are plain text.
   */
  onAttributeLinkClick?: (documentId: string, label: string) => void
  /**
   * Tag chips for this node (FS-655). Omitted on the Knowledge Graph page,
   * where nodes are documents rather than tagged equipment.
   */
  tags?: string[]
  /** Shows the Edit affordance beside the Tag section when provided. */
  onEditTags?: () => void
  /** Chip to outline — the tag currently under review. */
  activeTag?: string
  /** Footer button label. Defaults to the Knowledge Graph page's wording. */
  footerLabel?: string
  /**
   * Section wording, defaulted to the Knowledge Graph page's.
   *
   * Tag mapping's frames name the same sections differently — its nodes ARE
   * tags, so "Mapped tag" and "Tag sources" say what they hold, where the
   * generic "Tag" and "Connections" do not. Overridden per usage rather than
   * renamed globally, because the Knowledge Graph page has no design of its own
   * yet and should not drift on the back of this one.
   */
  title?: string
  attributesLabel?: string
  tagsLabel?: string
  connectionsLabel?: string
  /**
   * Render core attributes as plain label/value rows instead of a bordered
   * table. Tag mapping's frame shows them unboxed.
   */
  plainAttributes?: boolean
  /**
   * Render the footer even when the node has no attached documents. The
   * Knowledge Graph page's footer opens an attached file, so it hides without
   * one; Tag mapping's opens the sheet the tag sits on, which always exists.
   */
  alwaysShowFooter?: boolean
  /** Width/rail override — Tag mapping's panel is wider than the KG page's. */
  className?: string
  /**
   * Draw as a floating card over the canvas (DSGN-63 design: inset at the
   * right, rounded, shadowed) instead of a full-height side rail. The card
   * also drops the colon after attribute labels and uses the design's
   * larger chips and button.
   */
  floating?: boolean
  /**
   * Expand or collapse this node's children on the canvas (DSGN-63 radial
   * spec). Rendered only when given, i.e. when the node has children.
   */
  onToggleChildren?: () => void
  /** Whether the children are currently hidden; picks the button's wording. */
  childrenCollapsed?: boolean
}

const NodeDetailsPanel = ({
  node,
  connections,
  onClose,
  onConnectionClick,
  onConnectionSelect,
  selectedConnectionId,
  onOpenPreview,
  onAttributeLinkClick,
  tags,
  onEditTags,
  activeTag,
  footerLabel,
  title,
  attributesLabel,
  tagsLabel,
  connectionsLabel,
  plainAttributes,
  alwaysShowFooter,
  className,
  onToggleChildren,
  childrenCollapsed,
  floating,
}: Props) => {
  const { t } = useTranslation()
  if (!node) return null

  const attributes = node.attributes ?? [{ label: 'Name', value: node.label }]
  const attached = node.attachedDocuments ?? []
  const showFooter = !!onOpenPreview && (alwaysShowFooter || attached.length > 0)

  // An attribute naming files draws each as a link that opens it; one with no
  // document behind it (gone from the catalog) stays text.
  const renderValue = (attr: NodeAttribute) => {
    if (!attr.links || !onAttributeLinkClick) return attr.value
    return attr.links.map((link, i) => {
      const { documentId } = link
      return (
        <Fragment key={link.documentId ?? `${link.label}-${i}`}>
          {i > 0 && ', '}
          {documentId ? (
            <button
              type="button"
              onClick={() => onAttributeLinkClick(documentId, link.label)}
              title={t('knowledge_graph:node_details.open_file', { defaultValue: 'Open file' })}
              className="text-start text-[#34728d] underline-offset-2 hover:underline dark:text-[#a9dbef]"
            >
              {link.label}
            </button>
          ) : (
            <span>{link.label}</span>
          )}
        </Fragment>
      )
    })
  }

  return (
    <aside
      className={cn(
        floating
          ? 'absolute right-4 top-4 bottom-4 z-20 w-[440px] max-w-[calc(100%-2rem)] rounded-xl border border-border bg-background shadow-xl dark:bg-dark-card flex flex-col'
          : 'w-[420px] shrink-0 h-full border-l border-border bg-background dark:bg-dark-card flex flex-col',
        className,
      )}
      data-floating={floating ? 'true' : undefined}
    >
      <header className={cn('flex items-center justify-between border-b border-border shrink-0', floating ? 'px-4 h-12' : 'px-4 h-12')}>
        <span className={cn('font-semibold text-foreground', floating ? 'text-base' : 'text-sm')}>
          {title ??
            t('knowledge_graph:node_details.title', { defaultValue: 'Node Details' })}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground"
          aria-label={t('common:close', { defaultValue: 'Close' })}
        >
          <X className="size-4" />
        </button>
      </header>

      <div className={cn('flex-1 overflow-auto flex flex-col gap-6', floating ? 'px-4 py-4 gap-6' : 'px-4 py-4')}>
        {/* Core Attributes */}
        <section>
          <h3 className={cn('font-semibold text-foreground mb-2', floating ? 'text-sm mb-4' : 'text-sm')}>
            {attributesLabel ??
              t('knowledge_graph:node_details.core_attributes', {
                defaultValue: 'Core Attributes',
              })}
          </h3>
          {plainAttributes ? (
            <dl className={cn('grid gap-x-4', floating ? 'grid-cols-[minmax(0,36%)_minmax(0,1fr)] gap-y-5 pl-1.5 text-[13px]' : 'grid-cols-[minmax(0,44%)_minmax(0,1fr)] gap-y-2 text-sm')}>
              {attributes.map((attr, i) => (
                <Fragment key={`${attr.label}-${i}`}>
                  <dt className="text-muted-foreground">{floating ? attr.label : `${attr.label}:`}</dt>
                  <dd className="break-words text-foreground">{renderValue(attr)}</dd>
                </Fragment>
              ))}
            </dl>
          ) : (
          <div className="rounded-md border border-border overflow-hidden">
            <table className="w-full text-xs">
              <tbody>
                {attributes.map((attr, i) => (
                  <tr
                    key={`${attr.label}-${i}`}
                    className={i > 0 ? 'border-t border-border' : ''}
                  >
                    <td className="px-3 py-2 text-muted-foreground font-medium w-[44%] align-top bg-muted/30">
                      {attr.label}
                    </td>
                    <td className="px-3 py-2 text-foreground break-words">{renderValue(attr)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </section>

        {/* Attached Documents */}
        {attached.length > 0 && (
          <section>
            <h3 className={cn('font-semibold text-foreground mb-2', floating ? 'text-sm mb-3' : 'text-sm')}>
              {t('knowledge_graph:node_details.attached_documents', {
                defaultValue: 'Attached Documents',
              })}
            </h3>
            <div className="flex flex-wrap gap-2">
              {attached.map((d) => (
                <span
                  key={d}
                  className={cn(
                    'inline-flex items-center gap-1.5 border border-border text-foreground',
                    floating ? 'rounded-lg bg-background px-3 py-1.5 text-[13px]' : 'rounded-md bg-muted/40 px-2 py-1 text-xs',
                  )}
                >
                  {!floating && <FileText className="size-3 text-muted-foreground" />}
                  <span className="truncate max-w-[300px]">{d}</span>
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Tag (FS-655) — the tags carried by this node. Rendered as chips
            with a title attribute because a P&ID "tag" can be a merged blob of
            legend text hundreds of characters long. */}
        {tags && tags.length > 0 && (
          <section>
            <div className="mb-2 flex items-center justify-between">
              <h3 className={cn('font-semibold text-foreground', floating ? 'text-sm' : 'text-sm')}>
                {tagsLabel ??
                  t('knowledge_graph:node_details.tag', { defaultValue: 'Tag' })}
              </h3>
              {onEditTags && (
                <button
                  type="button"
                  onClick={onEditTags}
                  className="text-xs text-sky-700 hover:underline dark:text-sky-300"
                >
                  {t('common:edit', { defaultValue: 'Edit' })}
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => (
                <span
                  key={tag}
                  title={tag}
                  className={cn(
                    'inline-flex items-center border bg-background',
                    floating ? 'rounded-lg px-3 py-1.5 text-[13px]' : 'rounded-md px-2.5 py-1 text-xs',
                    tag === activeTag
                      ? 'border-sky-600 text-sky-700 dark:text-sky-300'
                      : 'border-border text-foreground',
                  )}
                >
                  <span className="truncate max-w-[220px]">{tag}</span>
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Connections */}
        <section>
          <h3 className={cn('font-semibold text-foreground mb-2', floating ? 'text-sm mb-3' : 'text-sm')}>
            {connectionsLabel ??
              t('knowledge_graph:node_details.connections', {
                defaultValue: 'Connections',
              })}
          </h3>
          {connections.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              {t('knowledge_graph:node_details.no_connections', {
                defaultValue: 'No connections',
              })}
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {connections.map((c) => {
                const selected = !!onConnectionSelect && c.id === selectedConnectionId
                return (
                  <button
                    key={c.id}
                    type="button"
                    aria-pressed={onConnectionSelect ? selected : undefined}
                    onClick={() => (onConnectionSelect ? onConnectionSelect(c.id) : onConnectionClick?.(c.targetId))}
                    className={cn(
                      'group inline-flex items-center gap-1.5 border transition-colors cursor-pointer',
                      selected
                        ? 'border-[#34728d] bg-[#e4f2fd] text-[#34728d] dark:border-[#7cc0dd] dark:bg-[#34728d]/25 dark:text-[#a9dbef]'
                        : 'border-border text-foreground hover:bg-muted hover:border-primary/40',
                      floating
                        ? cn('rounded-lg px-3 py-1.5 text-[13px]', !selected && 'bg-background')
                        : cn('rounded-md px-2 py-1 text-xs', !selected && 'bg-muted/40'),
                    )}
                    title={
                      onConnectionSelect
                        ? t('knowledge_graph:node_details.highlight_connection', { defaultValue: 'Highlight on graph' })
                        : t('knowledge_graph:node_details.go_to_connection', { defaultValue: 'Go to connection' })
                    }
                  >
                    <span className="truncate max-w-[340px]">{c.label}</span>
                    {!floating && <ArrowRight className="size-3 text-muted-foreground group-hover:text-foreground shrink-0" />}
                  </button>
                )
              })}
            </div>
          )}
        </section>
      </div>

      {(showFooter || onToggleChildren) && (
        <footer className={cn('border-t border-border flex justify-end gap-2 shrink-0', floating ? 'p-4' : 'p-3')}>
          {onToggleChildren && (
            <Button size="sm" variant="outline" onClick={onToggleChildren}>
              {childrenCollapsed
                ? t('knowledge_graph:node_details.expand_children', { defaultValue: 'Expand children' })
                : t('knowledge_graph:node_details.collapse_children', { defaultValue: 'Collapse children' })}
            </Button>
          )}
          {showFooter && (
          <Button size={floating ? 'default' : 'sm'} className={floating ? 'h-[34px] px-4 text-sm font-semibold' : undefined} onClick={onOpenPreview}>
            {footerLabel ??
              t('knowledge_graph:node_details.open_preview', {
                defaultValue: 'Open Preview Files',
              })}
          </Button>
          )}
        </footer>
      )}
    </aside>
  )
}

export default NodeDetailsPanel
